import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CartsByCartId } from '@comicrelief/data-models/schemas/payments/models/Cart';

export interface UrlOverridesState {
  cartOverride?: string,
  providersOverride?: string[],
  // -- Donate widget params --
  amountOverride?: number
  // While now named 'Journey' in this new build, keeping
  // this aligned with the param provided by the widget:
  givingTypeOverride?: string,
  affiliateOverride?: string,
  siteUrlOverride?: string,
  rowIdOverride?: string,
  popUpShownOverride?: boolean,
  clientOverride?: string,
  disableCartABTestOverride?: boolean,
  randomlyChosenCart?: string | undefined
}

const initialState: Readonly<UrlOverridesState> = {};

export const urlOverridesSlice = createSlice({
  name: 'urlOverrides',
  initialState,
  reducers: {
    setCartOverrideFromQuery(state: UrlOverridesState, action: PayloadAction<{ cartID: string, cartsData: CartsByCartId | undefined }>) {
      // Cased to match the format used by cartsData object, defaulting to our
      // override-supplied cart, just in case none of the criteria below is met:
      let finalCartID: string | undefined = action.payload?.cartID.toUpperCase();

      const allCartsData = action.payload?.cartsData;
      let thisCartData = allCartsData?.[finalCartID];
      let hasRandomlyChosen = false;

      // 1: First, check that we've not disabled A/B testing via its own URL param, and this cart has valid A/B config:
      if (!state.disableCartABTestOverride && thisCartData?.abTest?.active && thisCartData?.abTest?.carts) {
        // 2: Only run if we've NOT already chosen a random cart for this session, to ensure the
        //  user's not shown a potentially different cart when navigating back to the front page
        if (!state.randomlyChosenCart) {
          const abCarts = thisCartData.abTest.carts;

          // Randomly pick one of the available A/B carts; note that this `carts` object does also include the 'original' cart:
          finalCartID = abCarts[Math.floor(Math.random() * abCarts.length)];
          hasRandomlyChosen = true;
          // Confirm that _this_ cart exists in config while we're here:
          thisCartData = allCartsData?.[finalCartID];
        } else {
          // 2: Else, we've already chosen a randomised cart this session, so ensure we keep using it:
          finalCartID = state.randomlyChosenCart;
        }
      } else {
        // 1: Else, we don't need to do anything, and we'll use the param-supplied cartID verbatim, which will be validated later in useCart:
      }

      return {
        ...state,
        // Only attaches valid, non-null cartOverride, be it an A/B cart or not:
        ...(finalCartID && thisCartData) && {
          cartOverride: finalCartID
        },
        // Store any newly-produced random cartID into state to act as a flag to prevent re-randomising this session:
        ...(finalCartID && hasRandomlyChosen && thisCartData) && {
          randomlyChosenCart: finalCartID
        },
      };
    },

    setProvidersOverrideFromQuery(state: UrlOverridesState, action: PayloadAction<string>) {
      // Split providersOverride list (may be comma separated) into array (contains single item if no commas in query)
      const providersOverride = action.payload.toUpperCase().split(',');

      return {
        ...state,
        providersOverride,
      };
    },

    setClientOverrideFromQuery(state: UrlOverridesState, action: PayloadAction<string>) {
      const clientOverride = action.payload;
      return {
        ...state,
        clientOverride,
      };
    },

    setAmountOverrideFromQuery(state: UrlOverridesState, action: PayloadAction<string>) {
      const amountOverride = Number(action.payload);
      return {
        ...state,
        amountOverride,
      };
    },

    setGivingTypeOverrideFromQuery(state: UrlOverridesState, action: PayloadAction<string>) {
      const givingTypeOverride = action.payload;
      return {
        ...state,
        givingTypeOverride,
      };
    },

    setAffiliateOverrideFromQuery(state: UrlOverridesState, action: PayloadAction<string>) {
      const affiliateOverride = action.payload;
      return {
        ...state,
        affiliateOverride,
      };
    },

    setSiteUrlOverrideFromQuery(state: UrlOverridesState, action: PayloadAction<string>) {
      const siteUrlOverride = action.payload;
      return {
        ...state,
        siteUrlOverride,
      };
    },

    setRowIdOverrideFromQuery(state: UrlOverridesState, action: PayloadAction<string>) {
      const rowIdOverride = action.payload;
      return {
        ...state,
        rowIdOverride,
      };
    },

    // TO-DO: check with Hiral how to handle this within GTM
    setPopUpOverrideFromQuery(state: UrlOverridesState, action: PayloadAction<string>) {
      // Cast string to boolean
      const popUpShownOverride = action.payload === 'true';
      return {
        ...state,
        popUpShownOverride,
      };
    },
    setDisableCartABTestOverrideFromQuery(state: UrlOverridesState, action: PayloadAction<string>) {
      const disableCartABTestOverride = action.payload === 'true';
      return {
        ...state,
        disableCartABTestOverride,
      };
    },
  }
});

export const {
  setCartOverrideFromQuery,
  setProvidersOverrideFromQuery,
  setAmountOverrideFromQuery,
  setClientOverrideFromQuery,
  setGivingTypeOverrideFromQuery,
  setAffiliateOverrideFromQuery,
  setSiteUrlOverrideFromQuery,
  setRowIdOverrideFromQuery,
  setPopUpOverrideFromQuery,
  setDisableCartABTestOverrideFromQuery
} = urlOverridesSlice.actions;
